import { useEffect } from 'react';
import type { ComponentType } from 'react';

import type { Item } from 'types';
import type { CategoryDto } from 'components/Collection/pages/categories';
import TuxedosAndSuitsCopy from 'components/Collection/marketingCopy/tuxedos-and-suits';
import TiesCopy from 'components/Collection/marketingCopy/ties';
import AccessoriesCopy from 'components/Collection/marketingCopy/accessories';
import VestsAndCummerbundsCopy from 'components/Collection/marketingCopy/vests-and-cummerbunds';
import ShoesAndSocksCopy from 'components/Collection/marketingCopy/shoes-and-socks';
import ShirtsCopy from 'components/Collection/marketingCopy/shirts';
import Layout1x1 from 'components/Layout1x1';
import { PortableText, PortableTextProps } from 'components/sanity/serializers';
import DoubleHeader from 'components/typography/DoubleHeader';
import { useCollectionFilters } from 'hooks/useCollectionFilters';
import FilterComponent from 'partials/products/FilterComponent';
import CollectionList from 'partials/products/CollectionList';
import { productListFiltered } from 'utils/metrics';

const slugToAboutContent: { [key: string]: ComponentType } = {
  accessories: AccessoriesCopy,
  shirts: ShirtsCopy,
  'shoes-and-socks': ShoesAndSocksCopy,
  ties: TiesCopy,
  'tuxedos-and-suits': TuxedosAndSuitsCopy,
  'vests-and-cummerbunds': VestsAndCummerbundsCopy,
};

const AboutContent = ({ category }: { category: string | null }) => {
  if (!category || !(category in slugToAboutContent)) {
    return null;
  }

  const CopyComponent = slugToAboutContent[category];

  return (
    <div className="bg-gray-lighter py-64 sm:py-128">
      <CopyComponent />
    </div>
  );
};

type HeaderContentProps = {
  displayName: string;
  activeCategory: CategoryDto;
};

const headerContent = (props: HeaderContentProps) => (
  <div className="col-start-1 col-end-13 sm:col-start-4 md:col-start-3">
    <div className="mb-32 xs:mb-64">
      <DoubleHeader
        hero
        smallTitle={<h2>Collection</h2>}
        largeTitle={<h1>{props.displayName === 'Tuxedos & Suits' ? "Men's Suits and Tuxedos" : props.displayName}</h1>}
      />

      {props.activeCategory.name === 'preconfigured' && (
        <p className="mt-16 max-w-md">
          Choose from 25+ men's suit and tuxedo styles designed in trending colors and fashionable fits.
        </p>
      )}
    </div>
  </div>
);

type PortableTextContent = PortableTextProps['content'];

export type MarketingCopy = {
  collectionCta: PortableTextContent;
  collectionContent: PortableTextContent;
  collectionCategory?: string | null;
};

export type CategoryPageProps = {
  activeCategory: CategoryDto;
  products: Item[];
  marketingCopy?: MarketingCopy;
  pageType?: string;
};

const CategoryPage = ({ activeCategory, products, marketingCopy, pageType }: CategoryPageProps) => {
  const {
    filteredProducts,
    categoryFilters,
    colorFilters,
    patternFilters,
    procurementFilters,
    tieTypeFilters,
    toggleCategoryFilters,
    toggleColorFilters,
    togglePatternFilters,
    toggleProcurementFilters,
    toggleTieTypesFilters,
  } = useCollectionFilters({
    products,
    activeCategory,
  });

  const trackProductListFiltered = () => {
    productListFiltered(
      activeCategory.name,
      {
        categories: categoryFilters,
        colors: colorFilters,
        patterns: patternFilters,
        procurement: procurementFilters,
        tieTypes: tieTypeFilters,
      },
      filteredProducts
    );
  };

  useEffect(() => {
    trackProductListFiltered();
  }, [categoryFilters, colorFilters, patternFilters, procurementFilters, tieTypeFilters]);

  const isTuxAndSuitsPage = activeCategory.name === 'preconfigured';
  const shouldRenderMarketingContent = marketingCopy && activeCategory.slug === marketingCopy?.collectionCategory;
  const shouldRenderCalloutCards = isTuxAndSuitsPage && pageType !== 'subcategory';

  return (
    <>
      <div className="product-index-page container py-32 sm:py-64">
        <div className="row">
          {shouldRenderMarketingContent ? (
            <div className="col-start-1 col-end-13 sm:col-start-4 md:col-start-3">
              <div className="mb-32 xs:mb-64">
                <PortableText content={marketingCopy.collectionCta} />
              </div>
            </div>
          ) : (
            headerContent({ activeCategory: activeCategory, displayName: activeCategory.displayName })
          )}
        </div>
        <div className="row">
          <div className="col-start-1 col-end-13 mb-64 xs:col-end-4 sm:col-end-4 md:col-end-3">
            <FilterComponent
              activeCategory={activeCategory}
              categoryFilters={categoryFilters}
              colorFilters={colorFilters}
              patternFilters={patternFilters}
              procurementFilters={procurementFilters}
              products={products}
              tieTypeFilters={tieTypeFilters}
              toggleCategoryFilters={toggleCategoryFilters}
              toggleColorFilters={toggleColorFilters}
              togglePatternFilters={togglePatternFilters}
              toggleProcurementFilters={toggleProcurementFilters}
              toggleTieTypeFilters={toggleTieTypesFilters}
              visibleFilters={pageType === 'subcategory' ? ['procurement'] : undefined}
            />
          </div>

          <div className="col-start-1 col-end-13 xs:col-start-4 sm:col-start-4 md:col-start-3">
            {filteredProducts.length > 0 ? (
              <CollectionList
                products={filteredProducts}
                activeCategory={activeCategory}
                hasActiveFilter={
                  categoryFilters.length > 0 ||
                  colorFilters.length > 0 ||
                  patternFilters.length > 0 ||
                  procurementFilters.length > 0 ||
                  tieTypeFilters.length > 0
                }
                itemType={isTuxAndSuitsPage ? 'bundle' : 'product'}
                showCalloutCards={shouldRenderCalloutCards}
              />
            ) : (
              <div>
                <h3 className="text-h3">No matches</h3>
                <p>Try selecting different filters.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {shouldRenderMarketingContent ? (
        <div className="bg-gray-lighter py-64 sm:py-128">
          <Layout1x1>
            <PortableText content={marketingCopy.collectionContent} />
          </Layout1x1>
        </div>
      ) : (
        <AboutContent category={activeCategory.slug ?? null} />
      )}
    </>
  );
};

export default CategoryPage;
